import { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { request } from "../../config/request.ts";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Dashboard.scss";

interface ModuleStat {
  label: string;
  icon: string;
  total: number;
  active: number;
}

interface DashboardData {
  totalStaff: number;
  activeStaff: number;
  totalAlumni: number;
  activeAlumni: number;
  totalEvents: number;
  activeEvents: number;
  totalNews: number;
  activeNews: number;
  totalGalleries: number;
  totalFacilities: number;
  totalDepartments: number;
  totalUsers: number;
  recentNews: Array<{
    id: number;
    title: string;
    news_date: string;
    status: boolean;
  }>;
  upcomingEvents: Array<{
    id: number;
    title: string;
    event_date: string;
    event_location: string | null;
    status: boolean;
  }>;
  moduleStats: ModuleStat[];
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "—";
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const response = await request.get("dashboard/summary");
        if (active) setData(response.data.data);
      } catch (error) {
        console.error(error);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchDashboard();
    return () => {
      active = false;
    };
  }, []);

  const stats = [
    {
      type: "staff",
      title: "Total Staff",
      value: data?.totalStaff?.toLocaleString("en-IN") ?? "0",
      icon: "badge",
    },
    {
      type: "events",
      title: "Total Events",
      value: data?.totalEvents?.toLocaleString("en-IN") ?? "0",
      icon: "event_available",
    },
    {
      type: "news",
      title: "Total News",
      value: data?.totalNews?.toLocaleString("en-IN") ?? "0",
      icon: "newspaper",
    },
    {
      type: "alumni",
      title: "Total Alumni",
      value: data?.totalAlumni?.toLocaleString("en-IN") ?? "0",
      icon: "school",
    },
  ];

  const quickStats = [
    {
      label: "Departments",
      value: data?.totalDepartments ?? 0,
      icon: "account_tree",
    },
    {
      label: "Galleries",
      value: data?.totalGalleries ?? 0,
      icon: "photo_library",
    },
    {
      label: "Facilities",
      value: data?.totalFacilities ?? 0,
      icon: "account_balance",
    },
    {
      label: "Users",
      value: data?.totalUsers ?? 0,
      icon: "group",
    },
  ];

  const recentNews = data?.recentNews ?? [];
  const upcomingEvents = data?.upcomingEvents ?? [];
  const moduleStats = data?.moduleStats ?? [];

  return (
    <PageWrapper loading={loading}>
      <Box className="dashboard-page">
        <Box className="dashboard-header">
          <Typography variant="h4" className="dashboard-title">
            Dashboard Overview
          </Typography>
          <Typography variant="body2" className="dashboard-subtitle">
            Welcome back! Here's what's happening at Crescent Public School
            today.
          </Typography>
        </Box>

        <Box className="stats-grid">
          {stats.map((stat) => (
            <Box key={stat.type} className={`stat-card ${stat.type}`}>
              <Box className="stat-content">
                <Box className="stat-icon-wrapper">
                  <Box className={`stat-icon ${stat.type}`}>
                    <span className="material-symbols-outlined">{stat.icon}</span>
                  </Box>
                </Box>
                <Box className="stat-info">
                  <p className="stat-label">{stat.title}</p>
                  <p className="stat-value">{stat.value}</p>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <Box className="dashboard-sections">
          <Box className="section recent-news">
            <Box className="section-header">
              <h3>Recent News</h3>
            </Box>
            <Box className="feed-list" sx={{ maxHeight: 400, overflowY: "auto" }}>
              {recentNews.map((news) => (
                <Box key={news.id} className="feed-item">
                  <Box className="feed-info">
                    <p className="feed-title">{news.title}</p>
                    <p className="feed-sub">{formatDate(news.news_date)}</p>
                  </Box>
                  <span className={`feed-badge ${news.status ? "active" : "inactive"}`}>
                    {news.status ? "Active" : "Inactive"}
                  </span>
                </Box>
              ))}
              {recentNews.length === 0 && (
                <p className="empty-state">No news available yet.</p>
              )}
            </Box>
          </Box>

          <Box className="section upcoming-events">
            <Box className="section-header">
              <h3>Upcoming Events</h3>
            </Box>
            <Box className="feed-list" sx={{ maxHeight: 400, overflowY: "auto" }}>
              {upcomingEvents.map((event) => (
                <Box key={event.id} className="feed-item">
                  <Box className="feed-info">
                    <p className="feed-title">{event.title}</p>
                    <p className="feed-sub">
                      {formatDate(event.event_date)}
                      {event.event_location
                        ? ` • ${event.event_location}`
                        : ""}
                    </p>
                  </Box>
                  <span className="feed-badge upcoming">Upcoming</span>
                </Box>
              ))}
              {upcomingEvents.length === 0 && (
                <p className="empty-state">No upcoming events.</p>
              )}
            </Box>
          </Box>

          <Box className="section order-status">
            <Box className="section-header">
              <h3>Content Overview</h3>
            </Box>
            <Box className="status-bars">
              {moduleStats.map((item, idx) => (
                <Box key={idx} className="status-item">
                  <Box className="status-label">
                    <span>{item.label}</span>
                    <span className="status-count">
                      {item.active} / {item.total} active
                    </span>
                  </Box>
                  <Box className="status-bar">
                    <Box
                      className="status-fill"
                      style={{
                        width: `${item.total ? (item.active / item.total) * 100 : 0}%`,
                        background: "#3b82f6",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box className="dashboard-sections secondary">
          <Box className="section quick-stats">
            <Box className="section-header">
              <h3>Quick Stats</h3>
            </Box>
            <Box className="quick-stats-grid">
              {quickStats.map((stat) => (
                <Box key={stat.label} className="quick-stat">
                  <span className="material-symbols-outlined">{stat.icon}</span>
                  <p className="stat-value">{stat.value.toLocaleString("en-IN")}</p>
                  <p className="stat-label">{stat.label}</p>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}